// Display text for the measure tool's figures, ported from the Android app's MeasureFormatters.kt.
// Plain figures with unit symbols ("223 m", "1.23 km", "+33 m"); no translated words.

import { KilometerUnit, MeterUnit } from '../../../utils/helpers/MeasurementPref';
import { formatFixed, roundHalfUp } from './numbers';

/** The small distance unit, by Android's MeterUnits names. */
export type MeterUnits = 'Meter' | 'Feet' | 'Yards' | 'Furlongs' | 'Fathoms' | 'Inches';

/** The big distance unit, by Android's KilometerUnits names. */
export type KilometerUnits = 'Kilometer' | 'Miles' | 'NauticalMiles' | 'LightYears';

/** The walker's distance units: a small one (m / ft) and a big one (km / mi). */
export interface MeasureUnits {
  small: MeterUnits;
  big: KilometerUnits;
}

export const METRIC_UNITS: MeasureUnits = { small: 'Meter', big: 'Kilometer' };
export const IMPERIAL_UNITS: MeasureUnits = { small: 'Feet', big: 'Miles' };

/** Metres and kilometres, or feet and miles — for callers that only know `isMetric()`. */
export function measureUnits(isMetric: boolean): MeasureUnits {
  return isMetric ? METRIC_UNITS : IMPERIAL_UNITS;
}

const FROM_WEB_METER_UNIT: Record<MeterUnit, MeterUnits> = {
  [MeterUnit.Meter]: 'Meter',
  [MeterUnit.Feet]: 'Feet',
  [MeterUnit.Yards]: 'Yards',
  [MeterUnit.Furlongs]: 'Furlongs',
  [MeterUnit.Fathoms]: 'Fathoms',
  [MeterUnit.Inches]: 'Inches',
};

const FROM_WEB_KILOMETER_UNIT: Record<KilometerUnit, KilometerUnits> = {
  [KilometerUnit.Kilometers]: 'Kilometer',
  [KilometerUnit.Miles]: 'Miles',
  [KilometerUnit.NauticalMiles]: 'NauticalMiles',
  [KilometerUnit.LightYears]: 'LightYears',
};

/**
 * The walker's full unit preference, as Android builds it (`MeasureUnits(pref.meter(),
 * pref.kilometer())`): pass `MeasurementPrefUtility.preference()`.
 */
export function measureUnitsFromPref(pref: { meter(): MeterUnit; kilometer(): KilometerUnit }): MeasureUnits {
  return {
    small: FROM_WEB_METER_UNIT[pref.meter()] ?? 'Meter',
    big: FROM_WEB_KILOMETER_UNIT[pref.kilometer()] ?? 'Kilometer',
  };
}

const METER_MULTIPLIERS: Record<MeterUnits, number> = {
  Meter: 1.0,
  Feet: 3.280839895013123,
  Yards: 1.0936132983377078,
  Furlongs: 0.004970969537898671,
  Fathoms: 0.5468066491688539,
  Inches: 39.37007874015748,
};

const KILOMETER_MULTIPLIERS: Record<KilometerUnits, number> = {
  Kilometer: 1.0,
  Miles: 0.6213711922373339,
  NauticalMiles: 0.5399568034557235,
  LightYears: 0.00000000000010570008340247,
};

const METER_SYMBOLS: Record<MeterUnits, string> = {
  Meter: 'm', Feet: 'ft', Yards: 'yd', Furlongs: 'fur', Fathoms: 'ftm', Inches: 'in',
};

const KILOMETER_SYMBOLS: Record<KilometerUnits, string> = {
  Kilometer: 'km', Miles: 'mi', NauticalMiles: 'nmi', LightYears: 'ly',
};

export const meterToUnitMultiplier = (unit: MeterUnits): number => METER_MULTIPLIERS[unit];
export const kilometerToUnitMultiplier = (unit: KilometerUnits): number => KILOMETER_MULTIPLIERS[unit];
export const smallUnitSymbol = (unit: MeterUnits): string => METER_SYMBOLS[unit];
export const bigUnitSymbol = (unit: KilometerUnits): string => KILOMETER_SYMBOLS[unit];

/** The walker's hike-time pace. The defaults are exactly Naismith: 5 km/h plus 1 min per 10 m up. */
export interface MeasurePace {
  kmPerHour: number;
  metersPerHour: number;
}

export const NAISMITH_PACE: MeasurePace = { kmPerHour: 5.0, metersPerHour: 600.0 };

/** A walking time split for display: "6 min" when [hours] is 0, else "1 h 5 min". */
export interface MeasureDuration {
  hours: number;
  minutes: number;
}

/** U+2212, the minus sign the sheet uses for a descent ("−12 m"), not the ASCII hyphen. */
export const MINUS = '−';

/** Whole small units, never switching to the big unit: "312 m", "1024 ft". */
export function smallUnit(meters: number, small: MeterUnits): string {
  return `${Math.round(meters * meterToUnitMultiplier(small))} ${smallUnitSymbol(small)}`;
}

/**
 * The Distance figure: whole small units below 1000 m ("223 m", "731 ft"), otherwise the big unit
 * to two decimals ("1.23 km", "0.62 mi"). Cut over on the metres as they will print, so 999.6 m
 * reads "1.00 km" rather than "1000 m".
 */
export function distance(meters: number, units: MeasureUnits): string {
  if (Math.round(meters) < 1000) return smallUnit(meters, units.small);
  const big = meters / 1000 * kilometerToUnitMultiplier(units.big);
  return `${formatFixed(big, 2)} ${bigUnitSymbol(units.big)}`;
}

/**
 * The label pill's distance: the Distance figure's cut-over, so the two switch units together, but
 * one decimal to keep the pill short — "312 m", "985 ft", then "1.8 km", "12.8 mi".
 */
export function labelDistance(meters: number, units: MeasureUnits): string {
  if (Math.round(meters) < 1000) return smallUnit(meters, units.small);
  const big = meters / 1000 * kilometerToUnitMultiplier(units.big);
  return `${formatFixed(big, 1)} ${bigUnitSymbol(units.big)}`;
}

/** "312 m · 047°M" — the pill above the measured point. */
export function label(meters: number, units: MeasureUnits, bearingText: string): string {
  return `${labelDistance(meters, units)} · ${bearingText}`;
}

/**
 * The Elevation figure: signed change in whole small units, "+33 m" / "−12 m". The sign follows
 * the rounded value, so a 0.3 m dip reads "+0 m" rather than "−0 m".
 */
export function elevationChange(deltaMeters: number, small: MeterUnits): string {
  const rounded = Math.round(deltaMeters * meterToUnitMultiplier(small));
  const sign = rounded >= 0 ? '+' : MINUS;
  return `${sign}${Math.abs(rounded)} ${smallUnitSymbol(small)}`;
}

/** "4 m" for "GPS accuracy ±4 m". */
export function accuracy(meters: number, small: MeterUnits): string {
  return smallUnit(meters, small);
}

/**
 * Walking minutes for a distance and climb at [pace] — the app's HikeTime rule: 0.012 min/m at
 * 5 km/h plus 0.1 min per metre of ascent at 600 m/h, each scaled to the pace.
 */
export function walkingMinutes(distanceMeters: number, ascentMeters: number, pace: MeasurePace = NAISMITH_PACE): number {
  return distanceMeters * 0.012 * (5.0 / pace.kmPerHour) + Math.max(0.0, ascentMeters) * 0.10 * (600.0 / pace.metersPerHour);
}

/** Mockup `fmtTime`: at least a minute, then hours and minutes past the hour. */
export function duration(minutes: number): MeasureDuration {
  const whole = Math.max(1, roundHalfUp(minutes));
  return whole < 60 ? { hours: 0, minutes: whole } : { hours: Math.floor(whole / 60), minutes: whole % 60 };
}
